/**
 * StripPreview — the animated preview of a `strip`-kind asset/variant. A wide
 * frame strip rendered as a PNG reads as a filmstrip, not a character; this
 * draws ONE cell (from the strip's server-packed geometry) on a small canvas
 * and supports pointer/keyboard playback and single-frame inspection.
 *
 * Deliberately DUMB (plan: no hold/nonce logic portal-side): every state
 * loops, timing is a preview-only approximation — the shell's
 * `ANIMATION_PLAYBACK` table stays the one authority on in-game playback.
 *
 * Lifecycle care (races review):
 *  - No draw until the image has loaded (a partially-decoded image draws
 *    garbage); frame 0 draws statically the moment it has.
 *  - A source/geometry change remounts the preview: old pixels must not be
 *    mistaken for the selected replacement or cropped with its dimensions.
 *  - Every effect sets its cancel token BEFORE cancelling/re-queueing rAF:
 *    StudioGameHub unmounts the studio wholesale on tab switch, and a leaked
 *    rAF loop compounds per remount.
 */
import { useEffect, useRef, useState } from 'react';
import type { CreationFilmStripV1 } from '@djgg/creations';

/** Preview-only fps approximation per state (loops everything). NOT the
 *  shell's playback policy — just enough that idle breathes slowly and move
 *  reads as a gait in the studio. Unknown states fall back to the default. */
const PREVIEW_FPS: Record<string, number> = {
  idle: 2.5,
  move: 8,
  climb: 6,
  hang: 6,
  win: 6,
};
const DEFAULT_PREVIEW_FPS = 10;

export function StripPreview({
  url,
  strip,
  variant = 'interactive',
  playing = true,
}: {
  url: string;
  strip: CreationFilmStripV1;
  variant?: 'interactive' | 'cinematic';
  playing?: boolean;
}) {
  return <StripPreviewContent key={JSON.stringify([url, strip, variant])} url={url} strip={strip} variant={variant} playing={playing} />;
}

function StripPreviewContent({
  url,
  strip,
  variant,
  playing,
}: {
  url: string;
  strip: CreationFilmStripV1;
  variant: 'interactive' | 'cinematic';
  playing: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [onScreen, setOnScreen] = useState(true);
  const [hovered, setHovered] = useState(false);
  const [playback, setPlayback] = useState<'auto' | 'playing' | 'paused'>('auto');
  const [manualFrame, setManualFrame] = useState(0);
  const [shownFrame, setShownFrame] = useState(0);
  const [failed, setFailed] = useState(false);
  const [visible, setVisible] = useState(() => !document.hidden);
  const [reducedMotion, setReducedMotion] = useState(() => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
  // The load belongs to this keyed selection; loadedTick starts drawing only
  // after its packed geometry has been checked.
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [loadedTick, setLoadedTick] = useState(0);
  const cinematic = variant === 'cinematic';
  const running = visible && onScreen && loadedTick > 0 && strip.frames > 1 &&
    (cinematic
      ? playing && !reducedMotion
      : playback === 'playing' || (playback === 'auto' && hovered && !reducedMotion));

  useEffect(() => {
    const media = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const motion = () => setReducedMotion(media?.matches ?? false);
    const visibility = () => setVisible(!document.hidden);
    media?.addEventListener?.('change', motion);
    document.addEventListener('visibilitychange', visibility);
    return () => { media?.removeEventListener?.('change', motion); document.removeEventListener('visibilitychange', visibility); };
  }, []);

  useEffect(() => {
    if (!canvasRef.current || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(([entry]) => setOnScreen(entry.isIntersecting));
    observer.observe(canvasRef.current);
    return () => observer.disconnect();
  }, []);

  // Load only this selection; the keyed wrapper removes obsolete pixels.
  useEffect(() => {
    let canceled = false;
    const img = new Image();
    img.onload = () => {
      if (canceled) return; // unmounted or URL swapped again — drop this load
      if (img.naturalWidth !== strip.frames * strip.frameWidth || img.naturalHeight !== strip.frameHeight) {
        setFailed(true); return;
      }
      imgRef.current = img;
      setLoadedTick((n) => n + 1);
    };
    img.onerror = () => { if (!canceled) setFailed(true); };
    const timeout = window.setTimeout(() => { if (!canceled && !imgRef.current) setFailed(true); }, 8_000);
    img.src = url;
    return () => {
      clearTimeout(timeout);
      canceled = true;
      img.onload = null;
      img.onerror = null;
    };
  }, [url, strip.frames, strip.frameWidth, strip.frameHeight]);

  // Draw a selected frame, or cycle on explicit Play / motion-safe hover.
  useEffect(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const frames = Math.max(1, strip.frames);
    const fps = PREVIEW_FPS[strip.state] ?? DEFAULT_PREVIEW_FPS;

    let canceled = false;
    let raf = 0;
    const draw = (frame: number) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(
        img,
        frame * strip.frameWidth,
        0,
        strip.frameWidth,
        strip.frameHeight,
        0,
        0,
        canvas.width,
        canvas.height,
      );
    };
    draw(manualFrame);
    setShownFrame(manualFrame);
    if (running && frames > 1) {
      const start = performance.now();
      const tick = (now: number) => {
        if (canceled) return; // token checked BEFORE re-queueing
        const frame = (manualFrame + Math.floor(((now - start) / 1000) * fps)) % frames;
        draw(frame);
        setShownFrame(frame);
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }
    return () => {
      canceled = true; // set the token first…
      cancelAnimationFrame(raf); // …then kill the queued frame
    };
  }, [loadedTick, running, manualFrame, strip.frames, strip.frameWidth, strip.frameHeight, strip.state]);

  function selectFrame(frame: number) {
    setPlayback('paused');
    setManualFrame((frame + strip.frames) % strip.frames);
  }
  const label = `${strip.entityName} · ${strip.state}`;

  return (
    <div role="group" aria-label={`${label} animation preview`}
      className={cinematic ? 'strip-preview strip-preview--cinematic' : 'strip-preview'}
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, width: '100%', height: '100%', minHeight: 0 }}>
    <canvas
      ref={canvasRef}
      width={strip.frameWidth}
      height={strip.frameHeight}
      style={{ width: '100%', maxHeight: cinematic ? '100%' : 'calc(100% - 68px)', flex: '1 1 auto', minHeight: cinematic ? 160 : 0, objectFit: 'contain', imageRendering: 'pixelated' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      role="img" aria-label={`${label}, frame ${shownFrame + 1} of ${strip.frames}`}
      title={`${label} — preview loops only; gameplay controls timing`}
      data-testid={`strip-preview-${strip.entityName}-${strip.state}`}
      data-frames={strip.frames}
      data-frame={shownFrame}
    />
    {failed ? <span role="status">Preview unavailable: image or frame layout could not be loaded.</span>
      : !loadedTick ? <span role="status">Loading preview…</span> : !cinematic ? <>
      <div style={{ display: 'flex', gap: 4 }}>
        <button type="button" className="btn btn--sm" style={{ minWidth: 44, minHeight: 44, padding: 4 }} disabled={strip.frames < 2}
          onClick={() => selectFrame(shownFrame - 1)} aria-label={`Previous frame: ${label}`}>‹</button>
        <button type="button" className="btn btn--sm" style={{ minWidth: 56, minHeight: 44, padding: 4 }} disabled={strip.frames < 2}
          onClick={() => { setManualFrame(shownFrame); setPlayback(running ? 'paused' : 'playing'); }}>{running ? 'Pause' : 'Play'}</button>
        <button type="button" className="btn btn--sm" style={{ minWidth: 44, minHeight: 44, padding: 4 }} disabled={strip.frames < 2}
          onClick={() => selectFrame(shownFrame + 1)} aria-label={`Next frame: ${label}`}>›</button>
      </div>
      <span style={{ fontSize: 11 }}>Frame {shownFrame + 1}/{strip.frames} · preview loop</span>
    </> : null}
    </div>
  );
}
